import mapValues from 'lodash/mapValues';

import {SupportInfo as ApiSupportInfo} from 'app_api/billing/types';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';
import {apiClient} from 'common/util/api-client';
import {purgeFetchCache} from 'common/report/queries/utils/fetch-cache';

import {appApiUrl} from '../utils';

// Mirrors analytics.billing.models.SupportPlan in billing/models.py.
export const enum SupportPlan {
  McdonaldsCustom = `mcdonalds_custom`,
  Custom = `custom`,
  Gold = `gold`,
  Platinum = `platinum`,
  Premium = `premium`,
  Silver = `silver`,
  Standard = `standard`,
  Unmapped = `unmapped`,
}

function buildEndpoint(): string {
  return appApiUrl(`billing/user_support_info`);
}

function processResponse(apiSupportInfo: ApiSupportInfo | null): SupportInfo | null {
  if (!apiSupportInfo) {
    return null;
  }

  return {
    billingAccount: {
      id: apiSupportInfo.billing_account.id,
      recurringServiceArr: apiSupportInfo.billing_account.recurring_service_arr,
      arr: apiSupportInfo.billing_account.arr,
    },
    userAgeDays: apiSupportInfo.user_age_days,
    supportChatEnabled: apiSupportInfo.support_chat_enabled,
    supportPlan: apiSupportInfo.support_plan as SupportPlan,
    pylonAppId: apiSupportInfo.pylon_app_id,
    pylonEmailHash: apiSupportInfo.pylon_email_hash,
    frigadePublicKey: apiSupportInfo.frigade_public_key,
    frigadeAssistantId: apiSupportInfo.frigade_assistant_id,
  };
}

export type BillingAccount = {
  id: number;
  recurringServiceArr: number;
  arr: number;
};

export type SupportInfo = {
  billingAccount: BillingAccount;
  userAgeDays: number;
  supportChatEnabled: boolean;
  supportPlan: SupportPlan;
  pylonAppId: string;
  pylonEmailHash: string;
  // Frigade publishable key and assistant ID (public by design; empty string
  // means Frigade is switched off in this environment).
  frigadePublicKey: string;
  frigadeAssistantId: string;
};

// support info endpoint doesn't take an id. `id` is needed by ApiSingleValueResource
export const SUPPORT_INFO_SENTINEL_ID = `sentinel_id`;

export const supportInfoClient: ApiResourceClient<SupportInfo> = {
  index() {
    throw new Error(`supportInfoClient does not support index queries`);
  },

  get(id: string) {
    if (id !== SUPPORT_INFO_SENTINEL_ID) {
      throw new Error(`supportInfoClient: unexpected id ${SUPPORT_INFO_SENTINEL_ID}`);
    }
    const fetches = apiClient.hydratedGet<ApiSupportInfo>(buildEndpoint());
    // The cache promise resolves null on a cache miss. ApiResourceClient's
    // types don't surface that (hydratedGet types `cache` as Promise<T> for
    // the same reason), so cast the null pass-through to the declared shape.
    return mapValues(fetches, (fetch) =>
      fetch.then((apiSupportInfo) => processResponse(apiSupportInfo) as SupportInfo),
    );
  },

  purgeCache() {
    purgeFetchCache({everything: true, path: buildEndpoint()});
  },
};
